import {
  buildOrdersWhere,
  companyNameSql,
  type LineFilterScope,
  lifecycleRankSql,
  lineTracingStatusSql,
  type OrdersFilterParams,
  parseSort,
  type SortColumn,
  sortable,
  sortClause,
} from '../orders/list.js';
import type { DbTransactionCtx, OrderLineRow } from './types.js';

const LINE_TRACING_STATUS_SQL = lineTracingStatusSql('order_lines');

export const ORDER_LINE_LIST_FIELDS = [
  'order_lines.*',
  `${LINE_TRACING_STATUS_SQL} AS tracing_request_status`,
  'orders.id AS ord_id',
  'orders.order_number AS ord_order_number',
  'orders.order_type AS ord_order_type',
  'orders.buyer_company_id AS ord_buyer_company_id',
  'orders.supplier_company_id AS ord_supplier_company_id',
  'orders.order_date AS ord_order_date',
  'orders.updated_at AS ord_updated_at',
  'orders.updated_by_user_name AS ord_updated_by_user_name',
  'orders.tracing_status AS ord_tracing_status',
  'orders.tags AS ord_tags',
  'orders.source AS ord_source',
  'orders.buyer_facility_process AS ord_buyer_facility_process',
  'orders.supplier_facility_process AS ord_supplier_facility_process',
  'orders.is_archived AS ord_is_archived',
  'orders.tracing_attachment_template_id AS ord_tracing_attachment_template_id',
].join(', ');

const SINGLE_LINE_SCOPE: LineFilterScope = {
  matchLine: predicate =>
    `EXISTS (SELECT 1 FROM order_lines ol JOIN styles s ON s.id = ol.style_id WHERE ol.id = order_lines.id AND (${predicate}))`,
  tracingStatus: LINE_TRACING_STATUS_SQL,
  lineSearchColumns: ['ol.order_line_number', 'ol.external_id'],
};

export const ORDER_LINE_SORT_COLUMNS: Record<string, SortColumn> = {
  orderNumber: sortable('LOWER(orders.order_number)', false),
  orderType: sortable('orders.order_type'),
  buyer: sortable(companyNameSql('orders.buyer_company_id')),
  supplier: sortable(companyNameSql('orders.supplier_company_id')),
  orderDate: sortable('orders.order_date'),
  updatedAt: sortable('orders.updated_at', false),
  orderLine: sortable("NULLIF(LOWER(order_lines.order_line_number), '')"),
  externalId: sortable("NULLIF(LOWER(order_lines.external_id), '')"),
  orderedQuantity: sortable('order_lines.quantity', false),
  secondaryQuantity: sortable('order_lines.secondary_quantity'),
  source: sortable('orders.source'),
  tracingStatus: sortable(lifecycleRankSql(LINE_TRACING_STATUS_SQL)),
};

const LINE_ORDER = ['order_lines.line_no ASC', 'order_lines.id ASC'];

export function resolveOrderLinesOrderBy(sort: string | undefined): string[] {
  const parsed = parseSort(sort, ORDER_LINE_SORT_COLUMNS);
  if (!parsed) return LINE_ORDER;
  return [
    sortClause(parsed.column, parsed.direction),
    `order_lines.order_id ${parsed.direction}`,
    ...LINE_ORDER,
  ];
}

/** Optional filters/pagination for {@link listOrderLinesDb}. Without `sort` the
 *  lines come back in `line_no` order. */
export interface ListOrderLinesOpts
  extends Omit<OrdersFilterParams, 'companyId'> {
  /** Restrict to a single parent order. Omit to list across all the company's orders. */
  orderId?: string;
  /** Pagination window. Omit both to fetch every matching line (no window). */
  page?: number;
  count?: number;
}

export async function listOrderLinesDb(
  db: DbTransactionCtx,
  companyId: string,
  opts?: ListOrderLinesOpts,
): Promise<{ rows: OrderLineRow[]; totalCount: number }> {
  const { conditions, binds } = buildOrdersWhere(
    { ...opts, companyId },
    SINGLE_LINE_SCOPE,
  );

  if (opts?.orderId !== undefined) {
    conditions.push('order_lines.order_id = :orderId');
    binds.orderId = opts.orderId;
  }

  const join = {
    type: 'INNER',
    table: 'orders',
    on: 'order_lines.order_id = orders.id',
  };
  const orderBy = resolveOrderLinesOrderBy(opts?.sort);

  const paginated = opts?.page !== undefined && opts?.count !== undefined;
  if (!paginated) {
    const { rows } = await db.select<OrderLineRow>(
      {
        tableName: 'order_lines',
        fields: ORDER_LINE_LIST_FIELDS,
        join,
        where: { conditions },
        orderBy,
      },
      binds,
    );
    return { rows, totalCount: rows.length };
  }

  const { rows, count } = await db.select<OrderLineRow>(
    {
      tableName: 'order_lines',
      fields: ORDER_LINE_LIST_FIELDS,
      join,
      where: { conditions },
      orderBy,
      offset: (opts.page as number) * (opts.count as number),
      limit: opts.count,
      count: { field: 'order_lines.id', alias: 'count' },
    },
    binds,
  );

  return { rows, totalCount: count };
}
